import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMsalAuth } from './useMsalAuth';
import { getBatchBaseUrl } from '../config/trapi';
import type { BatchEnvironment } from '../config/trapi';
import type { BatchJob } from '../types/batch';

interface BatchJobsResponse {
  data: BatchJob[];
  has_more: boolean;
  first_id?: string;
  last_id?: string;
}

interface GetAllJobsParams {
  selectedApiPath?: string;
  selectedApiVersion: string;
  selectedEnvironment: BatchEnvironment;
  limit?: number;
  after?: string;
}

// Constants
const DEFAULT_REFETCH_INTERVAL = 30000; // 30 seconds
const DEFAULT_STALE_TIME = 10000; // 10 seconds
const DEFAULT_RETRY_COUNT = 2;

// Utilities
const normalizeApiPath = (apiPath: string): string => {
  return apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
};

const buildRequestHeaders = (token: string, userOid: string | null, apiPath?: string): Record<string, string> => {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (apiPath) {
    headers['x-trapi-api-path'] = normalizeApiPath(apiPath);
  }

  if (userOid) {
    headers['x-ms-user-oid'] = userOid;
  }

  return headers;
};

const buildQueryParams = (params: GetAllJobsParams): URLSearchParams => {
  const queryParams = new URLSearchParams({
    'api-version': params.selectedApiVersion,
  });

  if (params.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.after) {
    queryParams.append('after', params.after);
  }

  return queryParams;
};

// Hook
export function useAllBatchJobs(params: GetAllJobsParams) {
  const { getToken, getUserOid } = useMsalAuth();

  return useQuery({
    queryKey: ['allBatchJobs', params],
    queryFn: async (): Promise<BatchJobsResponse> => {
      let token: string;
      let userOid: string | null;

      try {
        token = await getToken();
        userOid = await getUserOid();
      } catch (error) {
        console.error('Error fetching authentication details:', error);
        throw new Error('Failed to retrieve authentication details. Please try again.');
      }

      if (!token || userOid === undefined) {
        throw new Error('Invalid authentication details. Please check your session and try again.');
      }
      const baseUrl = getBatchBaseUrl(params.selectedEnvironment);
      
      const headers = buildRequestHeaders(token, userOid, params.selectedApiPath);
      const queryParams = buildQueryParams(params);

      console.log('Fetching all batch jobs with params:', {
        baseUrl,
        apiPath: params.selectedApiPath || 'all',
        apiVersion: params.selectedApiVersion,
        limit: params.limit,
        after: params.after,
      });

      try {
        const response = await axios.get<BatchJobsResponse>(
          `${baseUrl}/batches?${queryParams.toString()}`,
          { headers }
        );

        console.log('Batch jobs response:', {
          jobCount: response.data.data?.length || 0,
          hasMore: response.data.has_more,
          jobs: response.data.data,
        });

        return response.data;
      } catch (error) {
        console.error('Error fetching batch jobs:', error);
        throw error; // Re-throw the error to let React Query handle it
      }
    },
    enabled: true,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
    staleTime: DEFAULT_STALE_TIME,
    retry: DEFAULT_RETRY_COUNT,
  });
}
